import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useEstateOptions } from '@/api/estate-options';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

import type { EstatesFiltersSchemaType } from '../schemas';
import { SelectController } from '@/components/form/controllers/select-controller';
import type { EstateFilterProps } from './estate-filter';
import {
  activeFilterFieldClassName,
  activeFilterInputClassName,
  hasActiveFilterValue,
} from './estate-filter-active-field';

export const EstateFilterPropertyDetails: FC<
  Pick<EstateFilterProps, 'searchParams' | 'creatorList'>
> = ({ creatorList, searchParams }) => {
  const { filterOwn } = searchParams;

  const { t } = useTranslation();

  const { data } = useEstateOptions();

  const methods = useFormContext<EstatesFiltersSchemaType>();
  const activeValues = {
    marketType: methods.watch('marketType'),
    usageType: methods.watch('usageType'),
    propertyType: methods.watch('propertyType'),
    heatingType: methods.watch('heatingType'),
    creatorId: methods.watch('creatorId'),
    flooring: methods.watch('flooring'),
    glazing: methods.watch('glazing'),
  };

  const getSelectWrapperClassName = (value: unknown) =>
    cn(hasActiveFilterValue(value) && activeFilterFieldClassName);

  const getSelectClassName = (value: unknown, extraClassName?: string) =>
    cn(
      'h-8 self-end bg-white text-[#768295] hover:border-[#07B4C0] [&_svg]:text-[#2F3A4A]',
      hasActiveFilterValue(value) && activeFilterInputClassName,
      extraClassName
    );

  return (
    <>
      <fieldset className="mb-5 grid grid-cols-1 gap-4">
        <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
        <SelectController
          methods={methods}
          name="marketType"
          wrapperClassName={getSelectWrapperClassName(activeValues.marketType)}
          label={t('marketingType')}
          placeholder={t('selectMarketingType')}
          size="sm"
          color="neutral"
          className={getSelectClassName(activeValues.marketType)}
          options={data?.marketingType || []}
        />
        <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
        <SelectController
          methods={methods}
          name="usageType"
          wrapperClassName={getSelectWrapperClassName(activeValues.usageType)}
          label={t('usage')}
          placeholder={t('selectUsageType')}
          size="sm"
          color="neutral"
          className={getSelectClassName(activeValues.usageType)}
          options={data?.usage || []}
        />
        <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
        <SelectController
          methods={methods}
          name="propertyType"
          wrapperClassName={getSelectWrapperClassName(activeValues.propertyType)}
          label={t('propertyType')}
          placeholder={t('selectPropertyType')}
          size="sm"
          color="neutral"
          className={getSelectClassName(activeValues.propertyType)}
          options={data?.propertyType || []}
        />
        <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
        <SelectController
          methods={methods}
          name="heatingType"
          wrapperClassName={getSelectWrapperClassName(activeValues.heatingType)}
          label={t('heatingType')}
          placeholder={t('selectHeatingType')}
          size="sm"
          color="neutral"
          className={getSelectClassName(activeValues.heatingType)}
          options={data?.heatingType || []}
        />
      </fieldset>
      <fieldset className={cn('mb-5 grid grid-cols-1 gap-4')}>
        <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
        {creatorList ? (
          <Controller
            {...methods}
            name="creatorId"
            render={({ field }) => (
              <Select
                label={t('author')}
                placeholder={t('selectCreator')}
                size="sm"
                wrapperClassName={getSelectWrapperClassName(activeValues.creatorId)}
                className={getSelectClassName(
                  activeValues.creatorId,
                  filterOwn ? '[&_svg]:hidden' : undefined
                )}
                color="neutral"
                options={creatorList}
                {...field}
                onChange={(ev) => {
                  if (!filterOwn) {
                    field.onChange(ev);
                  }
                }}
                value={field.value}
              />
            )}
          />
        ) : null}
        <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
        <SelectController
          methods={methods}
          name="flooring"
          wrapperClassName={getSelectWrapperClassName(activeValues.flooring)}
          label={t('flooring')}
          placeholder={t('selectFlooring')}
          size="sm"
          color="neutral"
          className={getSelectClassName(activeValues.flooring)}
          options={data?.flooring || []}
        />
        <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
        <SelectController
          methods={methods}
          name="glazing"
          wrapperClassName={getSelectWrapperClassName(activeValues.glazing)}
          label={t('glazing')}
          placeholder={t('selectGlazing')}
          size="sm"
          color="neutral"
          className={getSelectClassName(activeValues.glazing)}
          options={data?.glazing || []}
        />
      </fieldset>
    </>
  );
};
